import { useRef, useState } from "react";
import { Form, Container, Panel, Schema, Divider, PanelHeader, InputNumber, Stack, Input, SelectPicker, Message } from "rsuite";
import { useTranslation } from "react-i18next";
import Format, { DisableOverlay, SelectField, TextField } from "@client/components/FormControls";
import { useEditLock } from "@client/socket/useEditLock";
import { findTab, setTabName } from "@client/stores/tabsStore";
import { user as authUser } from '@client/stores/authStore'
import { useSyncedState } from "@client/utils/useSyncedState";
import { Button, TabListFooterErrorStyle, TabListFooterErrorWrapperStyle, TabListFooterStyle, TTabListFormData } from "@client/components/Controls";
import TenantModel, { ETenantStatus, tenantLabelList } from "@client/models/tenant";
import StatusInfo from "./StatusInfo";
import toastMessage from "@client/utils/toastMessage";
import { Capabilities, hasPermission } from "@shared/roles";

const { StringType } = Schema.Types

const tenantModel = new TenantModel()

type EditTenantProps = { tabId: string; onCreated?: (newSubscription: any) => void }

const EditTenant: React.FC<EditTenantProps> = ({ tabId, onCreated }) => {
    const { t } = useTranslation()
    const tab = findTab(tabId)
    if (!tab) return null
    const [formData, setFormData] = useSyncedState(tab, 'formData', tab.store.initialData)
    const formRef = useRef<TTabListFormData>(null)
    const format = Format()
    const [formError, setFormError] = useState({})
    const newRecord = !tab?.store?.id

    // Limit authority: an administrator may edit limits directly (interim "shop"
    // / support). The owner can only request more (outbound event to n8n).
    const isAdmin = hasPermission(Capabilities.tenant_write)

    // subscription limits are managed via local state (rsuite Form does not bind
    // nested names reliably).
    const initialSub = formData?.subscription || formData?.subscriptionId || {}
    // A plan is shown and sent only when one was loaded. Without, the fields
    // used to show 1 / 1 / paused — invented — and Apply wrote them into the
    // real plan. The admin tenant has none by design; any other tenant
    // without one is an error state (operator, 13.10.2026).
    const loadedSub = formData?.subscriptionId || formData?.subscription
    const hasSub = Boolean(loadedSub && typeof loadedSub === 'object' && loadedSub._id)
    const isAdminTenant = Boolean(formData?.isAdminTenant)
    const [maxUsers, setMaxUsers] = useState<number>(Number(initialSub.maxActiveUsers ?? 1))
    const [maxProjects, setMaxProjects] = useState<number>(Number(initialSub.maxActiveProjects ?? 1))
    const [subStatus, setSubStatus] = useState<string>(initialSub.status || ETenantStatus.Paused)

    // owner "request more" inputs
    const [reqUsers, setReqUsers] = useState<number>(Number(initialSub.maxActiveUsers ?? 1) + 1)
    const [reqProjects, setReqProjects] = useState<number>(Number(initialSub.maxActiveProjects ?? 1) + 1)
    const [note, setNote] = useState<string>('')

    const refetch = async () => {
        const res: any = await tenantModel.read(String(tab?.store?.id || ''))
        if (res?.data) {
            setFormData(res.data)
            tab.store.initialData = structuredClone(res.data)
            const sub = res.data.subscriptionId || res.data.subscription || {}
            setMaxUsers(Number(sub.maxActiveUsers ?? 1))
            setMaxProjects(Number(sub.maxActiveProjects ?? 1))
            setSubStatus(sub.status || ETenantStatus.Paused)
        }
    }

    const lock = useEditLock({
        table: 'tenant',
        url: 'tenant',
        id: String(tab?.store?.id || ''),
        tabId,
        userId: String(authUser.value?._id || ''),
        userName: String(authUser.value?.userName || ''),
        enabled: !newRecord,
        onAcquire: refetch,
        onUpdated: refetch,
    })

    const canEdit = isAdmin && (newRecord || lock.status === 'owned')

    const hasErrors = () => Object.keys(formError).length > 0

    const onSubmit = async (_checkStatus?: any, e?: any) => {
        e?.preventDefault?.()
        if (!isAdmin || newRecord) return

        const id = String(tab.store.id)
        const payload: any = {
            name: formData.name,
            status: formData.status,
        }
        if (hasSub && !isAdminTenant) {
            payload.subscription = {
                maxActiveUsers: Number(maxUsers),
                maxActiveProjects: Number(maxProjects),
                status: subStatus,
            }
        }

        const response: any = await tenantModel.update(id, payload)
        if (response?.data) {
            toastMessage(t('Successfully saved'))
            onCreated?.(response.data)
            tab.store.initialData = structuredClone(response.data)
            setTabName(tabId, String(response.data.name || '').trim())
        } else {
            setFormError({ message: t('Unknown Error') })
        }
    }

    const onRequestIncrease = async () => {
        const id = String(tab.store.id)
        const response: any = await tenantModel.requestIncrease(id, {
            requestedUsers: Number(reqUsers),
            requestedProjects: Number(reqProjects),
            note,
        })
        if (response?.data?.status === 'requested') {
            toastMessage(t('Your request has been sent'))
            setNote('')
        } else {
            setFormError({ message: t('Unknown Error') })
        }
    }

    const model = Schema.Model({
        name: StringType().isRequired(format.required(t('Name'))),
    })

    const SelectLabelWithInfo = () => (
        <>
            {t('Status')}
            <StatusInfo />
        </>
    )

    return (
        <Container className="edit-user-subscription">
            <Panel bordered>
                <Form
                    layout='horizontal'
                    className="subscription-form"
                    formValue={formData}
                    ref={formRef}
                    model={model}
                    onChange={setFormData}
                    onCheck={setFormError}
                    autoComplete="off"
                >
                    {/* tenant */}
                    <PanelHeader>{t('Tenant')}</PanelHeader>
                    <TextField name="name" value={formData.name} label={t('Name')} readOnly={!canEdit} />
                    <TextField name="slug" value={formData.slug} label={t('Slug')} readOnly />
                    {isAdmin && (
                        <SelectField
                            name="status"
                            label={<SelectLabelWithInfo />}
                            data={tenantLabelList(true) as any}
                            readOnly={!canEdit}
                        />
                    )}

                    {/* subscription — none at all for the admin tenant */}
                    {!isAdminTenant && <Divider color="var(--color-6)" />}
                    {!isAdminTenant && <PanelHeader>{t('Subscription')}</PanelHeader>}

                    {isAdminTenant ? null : !hasSub ? (
                        <Message type="error" showIcon>
                            {t('No subscription linked')}
                        </Message>
                    ) : isAdmin ? (
                        <DisableOverlay disabled={!canEdit} banner={lock.banner}>
                            <Form.Group>
                                <Form.ControlLabel>{t('Max active users')}</Form.ControlLabel>
                                <InputNumber min={1} value={maxUsers} onChange={(v) => setMaxUsers(Number(v))} />
                            </Form.Group>
                            <Form.Group>
                                <Form.ControlLabel>{t('Max active projects')}</Form.ControlLabel>
                                <InputNumber min={1} value={maxProjects} onChange={(v) => setMaxProjects(Number(v))} />
                            </Form.Group>
                            <Form.Group>
                                <Form.ControlLabel>{t('Subscription status')}</Form.ControlLabel>
                                <SelectPicker
                                    data={tenantLabelList(true) as any}
                                    value={subStatus}
                                    onChange={(v) => setSubStatus(String(v))}
                                    cleanable={false}
                                    searchable={false}
                                    style={{ width: 220 }}
                                />
                            </Form.Group>
                        </DisableOverlay>
                    ) : (
                        <>
                            <Form.Group>
                                <Form.ControlLabel>{t('Max active users')}</Form.ControlLabel>
                                <Input value={String(maxUsers)} readOnly />
                            </Form.Group>
                            <Form.Group>
                                <Form.ControlLabel>{t('Max active projects')}</Form.ControlLabel>
                                <Input value={String(maxProjects)} readOnly />
                            </Form.Group>

                            {/* owner: request more */}
                            <Divider color="var(--color-6)" />
                            <PanelHeader>{t('Request more')}</PanelHeader>
                            <Form.Group>
                                <Form.ControlLabel>{t('Requested users')}</Form.ControlLabel>
                                <InputNumber min={1} value={reqUsers} onChange={(v) => setReqUsers(Number(v))} />
                            </Form.Group>
                            <Form.Group>
                                <Form.ControlLabel>{t('Requested projects')}</Form.ControlLabel>
                                <InputNumber min={1} value={reqProjects} onChange={(v) => setReqProjects(Number(v))} />
                            </Form.Group>
                            <Form.Group>
                                <Form.ControlLabel>{t('Note')}</Form.ControlLabel>
                                <Input as="textarea" rows={2} value={note} onChange={(v) => setNote(v)} />
                            </Form.Group>
                            <Stack justifyContent="flex-end">
                                <Button appearance="ghost" onClick={onRequestIncrease} disabled={newRecord}>
                                    {t('Request more')}
                                </Button>
                            </Stack>
                        </>
                    )}

                    {isAdmin && (
                        <div style={TabListFooterStyle}>
                            {hasErrors() && (
                                <span style={TabListFooterErrorWrapperStyle}>
                                    <span style={TabListFooterErrorStyle}>{t('Check your inputs')}</span>
                                </span>
                            )}
                            <Button
                                onClick={onSubmit}
                                appearance="ghost"
                                disabled={!canEdit}
                            >
                                {t('Apply')}
                            </Button>
                        </div>
                    )}
                </Form>
            </Panel>
        </Container>
    )
}

export default EditTenant
